import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, MessageCircle, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Matches = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const res = await axios.get(`${API}/matches`, { withCredentials: true });
      setMatches(res.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563eb] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-gray-900">My Matches</h1>
          <div className="w-[72px]"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {matches.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-100 to-red-100 flex items-center justify-center mx-auto mb-4">
              <Heart className="w-10 h-10 text-pink-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No matches yet</h2>
            <p className="text-gray-600 mb-6">Start discovering to find your perfect match!</p>
            <Button
              onClick={() => navigate('/discovery')}
              data-testid="go-discover-button"
              className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-6 py-3 rounded-lg"
            >
              Start Discovering
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {matches.map((match) => (
              <div
                key={match.match.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer"
                onClick={() => navigate(`/chat/${match.match.id}`)}
                data-testid={`match-card-${match.match.id}`}
              >
                {/* Photo */}
                <div className="relative aspect-[4/3] bg-gray-200">
                  {match.profile?.photos && match.profile.photos.length > 0 ? (
                    <img
                      src={match.profile.photos[0]}
                      alt={match.user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <UserIcon className="w-20 h-20 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Unread badge */}
                  {match.unread_count > 0 && (
                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold">
                      {match.unread_count}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{match.user.name}</h3>
                      {match.profile?.specialty && (
                        <p className="text-sm text-gray-600">{match.profile.specialty}</p>
                      )}
                    </div>
                    <MessageCircle className="w-5 h-5 text-[#2563eb]" />
                  </div>

                  {match.last_message && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {match.last_message.content}
                    </p>
                  )}

                  {!match.last_message && (
                    <p className="text-sm text-gray-400 italic">Start a conversation...</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Matches;
